/**
 * OpenDyslexic for Chrome — swaps page fonts for the OpenDyslexic typeface
 *
 * A single WAR entry exposes the four OpenDyslexic `.otf` weights and a stylesheet to
 * https origins with no `use_dynamic_url`, so each font is fetchable from any secure
 * page at a fixed URL.
 *
 * The manifest also lists `assets/styles/core/opendyslexic.css`, but that path does not
 * exist in the package (the file actually ships under `assets/css/core/`, which is not
 * declared) — so the stylesheet WAR resolves to nothing and only the fonts are usable.
 * The primary signal fetches a font weight; serving it confirms the extension.
 *
 * Manifest verified against version 2026.4.28.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'cdnapgfjopgaggbmfgbiinmmbdcglnam';

export const opendyslexic: ExtensionDefinition = {
  id: ID,
  name: 'OpenDyslexic for Chrome',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Exposes the OpenDyslexic .otf font weights to https origins with no dynamic URL. ' +
    'The declared stylesheet path is broken in the package, so detection rides on the ' +
    'fonts — a single fetch, conclusive.',
  signals: [
    {
      id: 'war-font-regular',
      kind: 'web-accessible-resource',
      description:
        'Fetch assets/fonts/opendyslexic/regular.otf — the OpenDyslexic regular weight ' +
        'exposed to https origins at a fixed path.',
      weight: 1,
      run: () => probeFetch(ID, 'assets/fonts/opendyslexic/regular.otf'),
    },
    {
      id: 'war-font-bold',
      kind: 'web-accessible-resource',
      description:
        'Fetch assets/fonts/opendyslexic/bold.otf — the bold weight, corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'assets/fonts/opendyslexic/bold.otf'),
    },
    {
      id: 'war-font-italic',
      kind: 'web-accessible-resource',
      description: 'Fetch assets/fonts/opendyslexic/italic.otf — a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'assets/fonts/opendyslexic/italic.otf'),
    },
  ],
};
